import React, { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { pages } from "src/components/routes";
import { allPages, hasAccessToPage } from "src/navigation";
import { PAGES } from "src/types/navigation";
import { useRedirect } from "src/navigation/useRedirect";
import { useAppSelector } from "src/store/hooks";
import { UserRole } from "src/constants/roles";

// Публичные страницы, которые показываются и авторизованным (без редиректа на дашборд):
// на /plaid/done приходят из Plaid Hosted Link, на /connect — по ссылке от бухгалтера,
// в т.ч. из браузера, где уже есть сессия; /privacy открывают из форм и подвала.
const OPEN_TO_AUTHENTICATED: PAGES[] = [PAGES.PLAID_DONE, PAGES.CONNECT, PAGES.PRIVACY];

const AuthGate: React.FC<{ userRole: UserRole | null; scheme: PAGES }> = ({
  userRole,
  scheme,
}) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { redirect } = useRedirect();
  const pageConfig = pages[scheme];
  const pageAccessLength = pageConfig.access.length;

  useEffect(() => {
    if (!isAuthenticated && pageAccessLength > 0) {
      // Неавторизованный пытается зайти на приватную страницу
      redirect({ scheme: PAGES.LOGIN });
    } else if (
      isAuthenticated &&
      pageAccessLength === 0 &&
      !OPEN_TO_AUTHENTICATED.includes(scheme)
    ) {
      // Авторизованный пытается зайти на публичную страницу
      redirect({ scheme: PAGES.DASHBOARD });
    } else if (isAuthenticated && !hasAccessToPage(scheme, userRole)) {
      // Нет доступа по роли
      redirect({ scheme: PAGES.DASHBOARD });
    }
  }, [isAuthenticated, userRole, scheme, redirect, pageAccessLength]);

  // Проверяем доступ
  const hasAccess = hasAccessToPage(scheme, userRole);

  if (!hasAccess) {
    return null; // Редирект уже в useEffect
  }

  const Component = pageConfig.component;
  return <Component />;
};

interface PagesSwitchProps {
  userRole: UserRole | null;
}

export const PagesSwitch: React.FC<PagesSwitchProps> = ({ userRole }) => {
  const location = useLocation();
  const { redirect } = useRedirect();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (location.pathname === "/" || location.pathname === "") {
      if (isAuthenticated && userRole) {
        redirect({ scheme: PAGES.DASHBOARD });
      } else {
        redirect({ scheme: PAGES.LOGIN });
      }
    }
  }, [location.pathname, isAuthenticated, userRole, redirect]);

  return (
    <Routes
      location={{
        ...location,
        pathname: location.pathname.split("/~/")[0],
      }}
    >
      {allPages.map((scheme) => (
        <Route
          key={scheme}
          path={scheme}
          element={<AuthGate userRole={userRole} scheme={scheme} />}
        />
      ))}
    </Routes>
  );
};
